import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Bell, Mail, Megaphone, Calendar, Newspaper, MessageSquare, Users,
  Building2, Tag, CheckCircle2, ChevronRight, Loader2, RefreshCw, Trash2,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_META = {
  newsletter:     { Icon: Mail,         label: 'Newsletter',   color: '#002249', bg: 'bg-[#002249]/10', text: 'text-[#002249]' },
  announcement:   { Icon: Megaphone,    label: 'Announcement', color: '#E63946', bg: 'bg-[#E63946]/10', text: 'text-[#E63946]' },
  event:          { Icon: Calendar,     label: 'Event',        color: '#0d9488', bg: 'bg-teal-100',     text: 'text-teal-700' },
  blog:           { Icon: Newspaper,    label: 'Blog',         color: '#7c3aed', bg: 'bg-violet-100',   text: 'text-violet-700' },
  message:        { Icon: MessageSquare,label: 'Message',      color: '#0284c7', bg: 'bg-sky-100',      text: 'text-sky-700' },
  community:      { Icon: Users,        label: 'Community',    color: '#059669', bg: 'bg-emerald-100',  text: 'text-emerald-700' },
  membership:     { Icon: Tag,          label: 'Membership',   color: '#d97706', bg: 'bg-amber-100',    text: 'text-amber-700' },
  business:       { Icon: Building2,    label: 'Business',     color: '#475569', bg: 'bg-slate-100',    text: 'text-slate-700' },
  info:           { Icon: Bell,         label: 'Update',       color: '#475569', bg: 'bg-slate-100',    text: 'text-slate-700' },
};

const FILTERS = [
  { id: 'all',          label: 'All' },
  { id: 'unread',       label: 'Unread' },
  { id: 'read',         label: 'Read' },
  { id: 'newsletter',   label: 'Newsletters' },
  { id: 'event',        label: 'Events' },
  { id: 'blog',         label: 'Blog' },
  { id: 'message',      label: 'Messages' },
  { id: 'announcement', label: 'Announcements' },
  { id: 'membership',   label: 'Membership' },
  { id: 'community',    label: 'Community' },
];

const fmtDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString('en', { month: 'short', day: 'numeric', year: 'numeric' });
};

const NotificationsPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [unread, setUnread] = useState(0);
  const navigate = useNavigate();

  const fetchAll = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/notifications?limit=200`, { withCredentials: true });
      setItems(res.data.items || []);
      setUnread(res.data.unread_count || 0);
    } catch (err) {
      toast.error('Could not load notifications');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const visible = useMemo(() => {
    if (filter === 'all') return items;
    if (filter === 'unread') return items.filter((i) => !i.read);
    if (filter === 'read') return items.filter((i) => i.read);
    return items.filter((i) => (i.type || 'info') === filter);
  }, [items, filter]);

  const counts = useMemo(() => {
    const c = { all: items.length, unread: items.filter((i) => !i.read).length, read: items.filter((i) => i.read).length };
    items.forEach((i) => { c[i.type] = (c[i.type] || 0) + 1; });
    return c;
  }, [items]);

  const markRead = async (n) => {
    if (n.read) return;
    setItems((prev) => prev.map((it) => (it.id === n.id ? { ...it, read: true, read_at: new Date().toISOString() } : it)));
    setUnread((u) => Math.max(0, u - 1));
    try { await axios.put(`${API}/notifications/${n.id}/read`, {}, { withCredentials: true }); } catch { /* ignore */ }
  };

  const markAllRead = async () => {
    if (!unread) return;
    const now = new Date().toISOString();
    setItems((prev) => prev.map((it) => (it.read ? it : { ...it, read: true, read_at: now })));
    setUnread(0);
    try {
      await axios.put(`${API}/notifications/read-all`, {}, { withCredentials: true });
      toast.success('All notifications marked as read');
    } catch { toast.error('Could not mark all as read'); }
  };

  const deleteOne = async (n) => {
    setItems((prev) => prev.filter((it) => it.id !== n.id));
    if (!n.read) setUnread((u) => Math.max(0, u - 1));
    try { await axios.delete(`${API}/notifications/${n.id}`, { withCredentials: true }); } catch { /* ignore */ }
  };

  const handleClick = async (n) => {
    await markRead(n);
    const target = n.link || '/notifications';
    if (target.startsWith('http')) {
      window.location.href = target;
    } else {
      navigate(target);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="notifications-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-[#002249] tracking-tight">Notifications</h1>
              <p className="text-[13px] sm:text-[14px] text-slate-500 mt-1">
                {unread > 0 ? <><strong className="text-[#E63946]">{unread} unread</strong> · </> : null}
                {items.length} total
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={fetchAll}
                disabled={loading}
                className="h-9 px-3 inline-flex items-center gap-1.5 text-[12px] font-semibold border border-slate-200 rounded-lg text-slate-700 hover:bg-white"
                data-testid="notifications-refresh"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />Refresh
              </button>
              {unread > 0 && (
                <button
                  onClick={markAllRead}
                  className="h-9 px-3 inline-flex items-center gap-1.5 text-[12px] font-semibold bg-[#002249] hover:bg-[#001530] text-white rounded-lg"
                  data-testid="notifications-mark-all-read"
                >
                  <CheckCircle2 className="w-3.5 h-3.5" />Mark all as read
                </button>
              )}
            </div>
          </div>

          {/* Filter chips */}
          <div className="mt-4 flex gap-2 overflow-x-auto pb-1 -mx-4 px-4 sm:mx-0 sm:px-0" data-testid="notifications-filter-bar">
            {FILTERS.map((f) => {
              const active = filter === f.id;
              const count = counts[f.id] || 0;
              return (
                <button
                  key={f.id}
                  onClick={() => setFilter(f.id)}
                  className={`flex-shrink-0 h-8 px-3 rounded-full text-[12px] font-semibold transition-all border ${
                    active
                      ? 'bg-[#002249] text-white border-[#002249]'
                      : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'
                  }`}
                  data-testid={`notifications-filter-${f.id}`}
                >
                  {f.label}
                  {count > 0 && (
                    <span className={`ml-1.5 ${active ? 'text-white/80' : 'text-slate-400'}`}>{count}</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* List */}
        {loading ? (
          <div className="bg-white rounded-xl border border-slate-200 py-16 flex items-center justify-center">
            <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
          </div>
        ) : visible.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 py-16 text-center px-6" data-testid="notifications-empty">
            <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
              <Bell className="w-6 h-6 text-slate-400" />
            </div>
            <p className="text-[15px] font-bold text-slate-800">No notifications</p>
            <p className="text-[13px] text-slate-500 mt-1">
              {filter === 'all' ? "You're all caught up." : `Nothing matches the ${filter} filter.`}
            </p>
          </div>
        ) : (
          <ul className="bg-white rounded-xl border border-slate-200 overflow-hidden divide-y divide-slate-100" data-testid="notifications-list">
            {visible.map((n, idx) => {
              const meta = TYPE_META[n.type] || TYPE_META.info;
              const Icon = meta.Icon;
              return (
                <motion.li
                  key={n.id}
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.015 }}
                  className={`group ${n.read ? 'bg-white' : 'bg-[#002249]/[0.025]'}`}
                  data-testid={`notification-row-${n.id}`}
                >
                  <div className="flex items-start gap-3 px-4 sm:px-5 py-4 hover:bg-slate-50 transition-colors">
                    <button
                      onClick={() => handleClick(n)}
                      className="flex-1 min-w-0 flex items-start gap-3 text-left"
                      data-testid={`notification-click-${n.id}`}
                    >
                      <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full ${meta.bg} ${meta.text} flex-shrink-0 flex items-center justify-center`}>
                        <Icon className="w-4 h-4 sm:w-5 sm:h-5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`text-[10px] font-bold uppercase tracking-wider ${meta.text}`}>
                            {meta.label}
                          </span>
                          {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-[#E63946]" />}
                          <span className="text-[10.5px] text-slate-400">{fmtDate(n.created_at)}</span>
                        </div>
                        <p className={`mt-1 text-[14px] leading-snug ${n.read ? 'font-medium text-slate-700' : 'font-bold text-[#0f172a]'}`}>
                          {n.title}
                        </p>
                        {n.body && (
                          <p className="text-[12.5px] text-slate-500 leading-snug mt-1 line-clamp-2">{n.body}</p>
                        )}
                      </div>
                      <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-slate-500 mt-2 hidden sm:block" />
                    </button>

                    <button
                      onClick={(e) => { e.stopPropagation(); deleteOne(n); }}
                      className="text-slate-300 hover:text-rose-500 p-1.5 rounded-md opacity-0 group-hover:opacity-100 transition-opacity"
                      title="Remove"
                      data-testid={`notification-delete-${n.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </motion.li>
              );
            })}
          </ul>
        )}

        <p className="mt-6 text-[11.5px] text-slate-400 text-center">
          Want fewer of these? Adjust your <Link to="/portal/settings" className="text-[#002249] font-semibold hover:underline">notification preferences</Link>.
        </p>
      </div>
    </div>
  );
};

export default NotificationsPage;
